import { useEffect, useState } from 'react'
import { Button, Nav } from 'react-bootstrap'
import { Link, useLocation } from 'react-router-dom'
import { getUserAccess } from '../helpers'
import { t } from '../locale'
import { UserAccess } from '../react-app-env'
import { isMicrosoftEnabled } from '../service/microsoft'
import { useSubscription } from '../context/SubscriptionContext'

function KLayerNavigation(props: any) {
  const location = useLocation()
  const {
    credit: subscriptionCredit,
    planType: selectedType,
    planPeriod: selectedPeriod,
  } = useSubscription()

  const userAccess: UserAccess = getUserAccess()

  const getSectionFromPath = (pathname: string) => {
    if (
      pathname.startsWith('/clients') ||
      (pathname.startsWith('/client/') && !pathname.startsWith('/client/stammdaten'))
    )
      return 'clients'
    if (
      [
        '/company',
        '/user/billing',
        '/user/profile',
        '/company/abacus',
        '/company/bexio',
        '/company/vertec',
        '/company/swiss-transparenzregister',
        '/company/microsoft',
        '/company/sharepoint-app',
        '/company/settings',
        '/company/team',
      ].some(p => pathname.startsWith(p))
    )
      return 'settings'
    if (pathname.startsWith('/client/stammdaten')) return 'stammdaten'
    if (
      pathname.startsWith('/user') &&
      !pathname.startsWith('/user/billing') &&
      !pathname.startsWith('/user/profile') &&
      !pathname.startsWith('/user/logout')
    )
      return 'team'
    return ''
  }

  const sectionHeaderClass = (section: string) =>
    `d-flex align-items-center gap-2 text-decoration-none${openSections.includes(section) ? ' text-primary fw-bold' : ' text-draft'}`

  const OPEN_SECTIONS_KEY = 'navOpenSections'

  const getPersistedSections = (): string[] => {
    try {
      const stored = localStorage.getItem(OPEN_SECTIONS_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  const [openSections, setOpenSections] = useState<string[]>(() => {
    const persisted = getPersistedSections()
    const current = getSectionFromPath(location.pathname)
    if (current && !persisted.includes(current)) return [...persisted, current]
    return persisted
  })

  const [microsoftEnabled, setMicrosoftEnabled] = useState(true)

  useEffect(() => {
    isMicrosoftEnabled().then(setMicrosoftEnabled)
  }, [])

  useEffect(() => {
    const current = getSectionFromPath(location.pathname)
    if (current) {
      setOpenSections(prev => {
        if (prev.includes(current)) return prev
        const updated = [...prev, current]
        localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(updated))
        return updated
      })
    }
  }, [location.pathname])

  const toggleSection = (section: string) => {
    setOpenSections(prev => {
      const updated = prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
      localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(updated))
      return updated
    })
  }

  return (
    <>
      {!!props.logged && (
        <div
          className='offcanvas-md offcanvas-end mb-4'
          // With every nav section expanded the sidebar becomes the tallest
          // column; this keeps its last items (LogOut + credits card) clear of
          // the position:fixed footer in Layout.tsx.
          style={{ paddingBottom: '120px' }}
          tabIndex={-1}
          id='sidebarMenu'
          aria-labelledby='sidebarMenuLabel'
        >
          <div className='offcanvas-header'>
            <h5 className='offcanvas-title p-3' id='sidebarMenuLabel'>
              <img
                src='/KYC_logo_black.png'
                className='img-fluid navbar-logo'
                alt='DATA-Security | KYC'
              />
            </h5>
            <Button
              className='btn-close'
              data-bs-dismiss='offcanvas'
              data-bs-target='#sidebarMenu'
              aria-label='Close'
            ></Button>
          </div>
          <div className='offcanvas-body d-md-flex flex-column p-0 pt-lg-3'>
            <Nav className='flex-column'>
              <Nav.Item className='mt-2'>
                <a
                  className={sectionHeaderClass('clients')}
                  role='button'
                  aria-expanded={openSections.includes('clients')}
                  aria-controls='clientsCollapse'
                  onClick={() => toggleSection('clients')}
                >
                  <i className='bi bi-people-fill'></i>
                  {t('Clients')}
                  <span className='ms-auto'>
                    <i
                      className={`bi bi-chevron-down rotate-icon${openSections.includes('clients') ? '' : ' collapsed'}`}
                    ></i>
                  </span>
                </a>
                <div
                  className={`collapse${openSections.includes('clients') ? ' show' : ''}`}
                  id='clientsCollapse'
                >
                  <ul className='ms-3 text-primary'>
                    <li className='nav-item'>
                      <Link
                        className='nav-link ps-0'
                        to={`/clients?clientStatus=${true}&&deleted=empty`}
                      >
                        {t('active')}
                      </Link>
                    </li>
                    <li className='nav-item'>
                      <Link
                        className='nav-link ps-0'
                        to={`/clients?clientStatus=${false}&&deleted=empty`}
                      >
                        {t('inactive')}
                      </Link>
                    </li>
                    <li className='nav-item'>
                      <Link
                        className='nav-link ps-0'
                        to='/clients?clientStatus=${}&&deleted=notEmpty'
                      >
                        {t('deleted')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </Nav.Item>
            </Nav>
            <Nav className='flex-column'>
              <Nav.Item>
                {!!userAccess && userAccess.permissions.admin && (
                  <>
                    <a
                      className={`mt-2 ${sectionHeaderClass('settings')}`}
                      role='button'
                      aria-expanded={openSections.includes('settings')}
                      aria-controls='settingsCollapse'
                      onClick={() => toggleSection('settings')}
                    >
                      <i className='bi bi-sliders2'></i>
                      {t('Settings')}
                      <span className='ms-auto'>
                        <i
                          className={`bi bi-chevron-down rotate-icon${openSections.includes('settings') ? '' : ' collapsed'}`}
                        ></i>
                      </span>
                    </a>
                    <div
                      className={`collapse${openSections.includes('settings') ? ' show' : ''}`}
                      id='settingsCollapse'
                    >
                      <ul className='ms-3 text-primary'>
                        <>
                          {userAccess.permissions.main && (
                            <>
                              <li className='nav-item'>
                                <Link className='nav-link ps-0' to='/company'>
                                  {t('Points')}
                                </Link>
                              </li>
                              <li className='nav-item'>
                                <Link className='nav-link ps-0' to='/user/billing'>
                                  {t('billing')}
                                </Link>
                              </li>
                            </>
                          )}
                          <li className='nav-item'>
                            <Link className='nav-link ps-0' to='/user/profile'>
                              {t('profile')}
                            </Link>
                          </li>
                        </>

                        <li className='nav-item'>
                          <a
                            className='nav-link ps-0 d-flex align-items-center gap-2 text-decoration-none'
                            role='button'
                            aria-expanded={openSections.includes('importData')}
                            aria-controls='importDataCollapse'
                            onClick={() => toggleSection('importData')}
                          >
                            {t('importData')}
                            <span className='ms-auto'>
                              <i
                                className={`bi bi-chevron-down rotate-icon${openSections.includes('importData') ? '' : ' collapsed'}`}
                              ></i>
                            </span>
                          </a>

                          <div
                            className={`collapse${openSections.includes('importData') ? ' show' : ''}`}
                            id='importDataCollapse'
                          >
                            <ul className='text-primary'>
                              <li className='nav-item'>
                                <Link className='nav-link ps-0' to='/company/abacus'>
                                  {t('abacusIntegration')}
                                </Link>
                              </li>
                              <li className='nav-item'>
                                <Link className='nav-link ps-0' to='/company/bexio'>
                                  {t('bexioIntegration')}
                                </Link>
                              </li>
                              <li className='nav-item'>
                                <Link className='nav-link ps-0' to='/company/vertec'>
                                  {t('vertecIntegration')}
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </li>

                        <li className='nav-item'>
                          <Link className='nav-link ps-0' to='/company/swiss-transparenzregister'>
                            {t('transparenzChMenuTitle')}
                          </Link>
                        </li>

                        {microsoftEnabled && (
                          <>
                            <li className='nav-item'>
                              <Link className='nav-link ps-0' to='/company/microsoft'>
                                {t('microsoftMenuTitle')}
                              </Link>
                            </li>

                            <li className='nav-item'>
                              <Link className='nav-link ps-0' to='/company/sharepoint-app'>
                                {t('microsoftSharePointAppMenuTitle')}
                              </Link>
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                  </>
                )}
              </Nav.Item>
              <Nav.Item>
                <a
                  className={`mt-2 ${sectionHeaderClass('stammdaten')}`}
                  role='button'
                  aria-expanded={openSections.includes('stammdaten')}
                  aria-controls='stammdatenCollapse'
                  onClick={() => toggleSection('stammdaten')}
                >
                  <i className='bi bi-card-list'></i>
                  {t('Stammdaten')}
                  <span className='ms-auto'>
                    <i
                      className={`bi bi-chevron-down rotate-icon${openSections.includes('stammdaten') ? '' : ' collapsed'}`}
                    ></i>
                  </span>
                </a>
                <div
                  className={`collapse${openSections.includes('stammdaten') ? ' show' : ''}`}
                  id='stammdatenCollapse'
                >
                  <ul className='ms-3 text-primary'>
                    <li className='nav-item'>
                      <Link className='nav-link ps-0' to={`/client/stammdaten`}>
                        {t('stammdaten')}
                      </Link>
                    </li>
                    {!!userAccess && userAccess.permissions.admin && (
                      <>
                        <li className='nav-item'>
                          <Link
                            className='nav-link ps-0'
                            to={`/client/stammdaten/required/company`}
                          >
                            {t('companyRequired')}
                          </Link>
                        </li>
                        <li className='nav-item'>
                          <Link className='nav-link ps-0' to={`/client/stammdaten/required/person`}>
                            {t('personRequired')}
                          </Link>
                        </li>
                        <li className='nav-item'>
                          <Link className='nav-link ps-0' to={`/client/stammdaten/formular/list`}>
                            {t('formular')}
                          </Link>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </Nav.Item>
              {!!userAccess && userAccess.permissions.admin && (
                <Nav.Item>
                  <a
                    className={`mt-2 ${sectionHeaderClass('team')}`}
                    role='button'
                    aria-expanded={openSections.includes('team')}
                    aria-controls='teamCollapse'
                    onClick={() => toggleSection('team')}
                  >
                    <i className='bi bi-person-workspace'></i>
                    {t('team')}
                    <span className='ms-auto'>
                      <i
                        className={`bi bi-chevron-down rotate-icon${openSections.includes('team') ? '' : ' collapsed'}`}
                      ></i>
                    </span>
                  </a>
                  <div
                    className={`collapse${openSections.includes('team') ? ' show' : ''}`}
                    id='teamCollapse'
                  >
                    <ul className='ms-3 text-primary'>
                      <li className='nav-item'>
                        <Link className='nav-link ps-0' to={`/user?active=true`}>
                          {t('users')}
                        </Link>
                      </li>

                      <li className='nav-item'>
                        <Link className='nav-link ps-0' to={`/user?active=false`}>
                          {t('inactiveUsers')}
                        </Link>
                      </li>
                      <li className='nav-item'>
                        <Link className='nav-link ps-0' to={`/company/team`}>
                          {t('teamSettings')}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </Nav.Item>
              )}
              <Nav.Item>
                <Link
                  className={`mt-2 ${sectionHeaderClass('team')} d-flex align-items-center gap-2 text-decoration-none`}
                  to='/user/logout'
                >
                  <i className='bi bi-box-arrow-right'></i>
                  {t('LogOut')}
                </Link>
              </Nav.Item>
              <Nav.Item>
                <div className='credit-card ms-1 mt-4'>
                  <div className='credit-card-first-content'>
                    <span>{t('availableCredits')}</span>
                    <div className='fs-5 text-primary fw-bold'>{subscriptionCredit}</div>
                  </div>
                  <div className='credit-card-second-content  text-primary'>
                    <span>
                      <strong>{t('plan')}:</strong> {t(selectedType)}
                    </span>
                    <span>
                      {' '}
                      <strong>{t('period')}:</strong> {t(selectedPeriod)} {t('days')}
                    </span>
                  </div>
                </div>
              </Nav.Item>
            </Nav>
          </div>
        </div>
      )}
    </>
  )
}

export default KLayerNavigation
